import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  EyeOff, 
  Clock,
  Calendar,
  Check,
  X,
  AlertCircle,
  RefreshCw,
  Sun,
  Moon,
  Sunrise,
  Sunset
} from 'lucide-react';

const ServiceHoursManagement = () => {
  const [serviceHours, setServiceHours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingHour, setEditingHour] = useState(null);
  const [notification, setNotification] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(() =>
    typeof document !== "undefined"
      ? document.documentElement.classList.contains("dark")
      : false,
  );
  const [formData, setFormData] = useState({
    day_of_week: 1, // Lundi par défaut
    start_time: '08:00',
    end_time: '18:00',
    service_name: '',
    is_active: true,
    sort_order: 0
  });

  // Jours de la semaine (0 = Dimanche, 1 = Lundi, etc.)
  const daysOfWeek = [
    { value: 1, name: 'Lundi', short: 'Lun', icon: Sun },
    { value: 2, name: 'Mardi', short: 'Mar', icon: Sun },
    { value: 3, name: 'Mercredi', short: 'Mer', icon: Sun },
    { value: 4, name: 'Jeudi', short: 'Jeu', icon: Sun },
    { value: 5, name: 'Vendredi', short: 'Ven', icon: Sun },
    { value: 6, name: 'Samedi', short: 'Sam', icon: Sunset },
    { value: 0, name: 'Dimanche', short: 'Dim', icon: Sunrise }
  ];

  // Heures prédéfinies pour faciliter la saisie
  const commonHours = [
    '06:00', '07:00', '08:00', '09:00', '10:00', '11:00',
    '12:00', '13:00', '14:00', '15:00', '16:00', '17:00',
    '18:00', '19:00', '20:00', '21:00', '22:00', '23:00',
    '00:00', '01:00', '02:00', '03:00', '04:00', '05:00'
  ];

  useEffect(() => {
    loadServiceHours();
  }, []);

  useEffect(() => {
    const target = document.documentElement;
    const syncTheme = () => setIsDarkMode(target.classList.contains("dark"));
    syncTheme();
    const observer = new MutationObserver(syncTheme);
    observer.observe(target, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);

  const containerBg = isDarkMode
    ? "bg-gradient-to-br from-[#050b18] via-[#0f1c33] to-[#050b18] text-slate-100"
    : "bg-gradient-to-br from-slate-50 via-white to-slate-100 text-slate-900";
  const cardClasses = isDarkMode
    ? "bg-white/5 border border-white/10 shadow-2xl shadow-black/40"
    : "bg-white border border-slate-200 shadow-xl shadow-slate-200/60";
  const inputClasses = isDarkMode
    ? "bg-white/5 border border-white/10 text-slate-100 focus:border-amber-400 focus:ring-2 focus:ring-amber-400/30"
    : "bg-white border border-slate-300 text-slate-800 focus:border-amber-500 focus:ring-2 focus:ring-amber-200";
  const subtleText = isDarkMode ? "text-slate-400" : "text-slate-500";
  const primaryButtonClasses =
    "bg-gradient-to-r from-amber-400 to-orange-500 text-white px-4 py-2 rounded-xl font-semibold shadow-lg hover:shadow-amber-500/30 transition";
  const ghostButtonClasses = isDarkMode
    ? "border border-white/15 bg-white/5 text-slate-100 px-4 py-2 rounded-xl hover:bg-white/10 transition"
    : "border border-slate-200 bg-white text-slate-700 px-4 py-2 rounded-xl hover:bg-slate-50 transition";
  const timelineLineClass = isDarkMode ? "bg-amber-400/40" : "bg-amber-500/30";
  const timelineDotBorder = isDarkMode ? "border-[#050b18]" : "border-white";

  // Fonction pour afficher les notifications
  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const loadServiceHours = async (showRefreshIndicator = false) => {
    try {
      if (showRefreshIndicator) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      
      console.log('🔄 Chargement des horaires de service...');
      const response = await fetch('/api/service-hours');
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Horaires de service reçus:', data);
        setServiceHours(data);
        
        if (showRefreshIndicator) {
          showNotification('Liste des horaires mise à jour', 'success');
        }
      } else {
        console.error('❌ Erreur lors du chargement des horaires:', response.status);
        showNotification('Erreur lors du chargement des horaires', 'error');
      }
    } catch (error) {
      console.error('❌ Erreur API horaires de service:', error);
      showNotification('Erreur de connexion lors du chargement', 'error');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Validation des données
      if (!formData.start_time || !formData.end_time) {
        showNotification('Les heures de début et fin sont obligatoires', 'error');
        return;
      }

      // Préparer les données avec l'ID si on modifie
      const dataToSend = editingHour 
        ? { ...formData, id: editingHour.id }
        : formData;

      console.log('🔄 Sauvegarde horaire de service:', dataToSend);

      const response = await fetch('/api/service-hours', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(dataToSend),
      });

      if (response.ok) {
        const result = await response.json();
        console.log('✅ Horaire de service sauvegardé:', result);
        
        // Recharger les données et fermer le modal
        await loadServiceHours(true);
        handleCloseModal();
        
        showNotification(
          editingHour ? 'Horaire modifié avec succès' : 'Horaire ajouté avec succès',
          'success'
        );
      } else {
        const errorData = await response.json();
        console.error('❌ Erreur lors de la sauvegarde:', errorData);
        showNotification('Erreur: ' + (errorData.error || 'Erreur inconnue'), 'error');
      }
    } catch (error) {
      console.error('❌ Erreur:', error);
      showNotification('Erreur de connexion lors de la sauvegarde', 'error');
    }
  };

  const handleEdit = (hour) => {
    setEditingHour(hour);
    setFormData({
      day_of_week: hour.day_of_week,
      start_time: hour.start_time,
      end_time: hour.end_time,
      service_name: hour.service_name || '',
      is_active: hour.is_active,
      sort_order: hour.sort_order || 0
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    const hour = serviceHours.find(h => h.id === id);
    const dayName = daysOfWeek.find(d => d.value === hour?.day_of_week)?.name || 'Inconnu';
    
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer l'horaire du ${dayName} (${hour?.start_time} - ${hour?.end_time}) ?`)) {
      try {
        const response = await fetch(`/api/service-hours/${id}`, {
          method: 'DELETE',
        });

        if (response.ok) {
          console.log('✅ Horaire de service supprimé:', id);
          await loadServiceHours(true);
          showNotification('Horaire supprimé avec succès', 'success');
        } else {
          const errorData = await response.json();
          console.error('❌ Erreur lors de la suppression:', errorData);
          showNotification('Erreur lors de la suppression: ' + (errorData.error || 'Erreur inconnue'), 'error');
        }
      } catch (error) {
        console.error('❌ Erreur:', error);
        showNotification('Erreur de connexion lors de la suppression', 'error');
      }
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      const hour = serviceHours.find(h => h.id === id);
      console.log('🔄 Changement statut horaire de service:', id);
      
      const response = await fetch(`/api/service-hours/${id}/toggle`, {
        method: 'PATCH',
      });

      if (response.ok) {
        const result = await response.json();
        console.log('✅ Statut modifié:', result);
        await loadServiceHours(true);
        
        const newStatus = !hour?.is_active;
        showNotification(
          `Horaire ${newStatus ? 'activé' : 'désactivé'} avec succès`,
          'success'
        );
      } else {
        const errorData = await response.json();
        console.error('❌ Erreur lors du changement de statut:', errorData);
        showNotification('Erreur lors du changement de statut: ' + (errorData.error || 'Erreur inconnue'), 'error');
      }
    } catch (error) {
      console.error('❌ Erreur:', error);
      showNotification('Erreur de connexion lors du changement de statut', 'error');
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingHour(null);
    setFormData({
      day_of_week: 1,
      start_time: '08:00',
      end_time: '18:00',
      service_name: '',
      is_active: true,
      sort_order: 0
    });
  };

  const getDayName = (dayOfWeek) => {
    const day = daysOfWeek.find(d => d.value === dayOfWeek);
    return day?.name || 'Jour inconnu';
  };

  const isTimeCrossDay = (startTime, endTime) => {
    return startTime > endTime;
  };

  const formatTimeRange = (startTime, endTime) => {
    if (isTimeCrossDay(startTime, endTime)) {
      return `${startTime} → ${endTime} (+1j)`;
    }
    return `${startTime} → ${endTime}`;
  };

  // Grouper les horaires par jour
  const groupedHours = serviceHours.reduce((groups, hour) => {
    const day = hour.day_of_week;
    if (!groups[day]) {
      groups[day] = [];
    }
    groups[day].push(hour);
    return groups;
  }, {});

  if (loading) {
    return (
      <div className={`min-h-screen px-4 py-6 ${containerBg}`}>
        <div className="flex items-center justify-center h-full">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 border-4 border-amber-400/60 border-t-transparent rounded-full animate-spin"></div>
            <span className="text-lg font-medium">
              Chargement des horaires de service...
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen px-4 py-6 ${containerBg}`}>
      {/* Notifications */}
      {notification && (
        <div
          className={`fixed top-4 right-4 z-50 rounded-2xl px-4 py-3 shadow-2xl transition-all duration-300 ${
            notification.type === "success"
              ? isDarkMode
                ? "bg-emerald-500/15 text-emerald-100 border border-emerald-400/40"
                : "bg-emerald-50 text-emerald-800 border border-emerald-200"
              : isDarkMode
                ? "bg-rose-500/15 text-rose-100 border border-rose-400/40"
                : "bg-rose-50 text-rose-700 border border-rose-200"
          }`}
        >
          <div className="flex items-center gap-2 text-sm font-medium">
            {notification.type === "success" ? (
              <Check size={18} />
            ) : (
              <AlertCircle size={18} />
            )}
            <span>{notification.message}</span>
          </div>
        </div>
      )}

      <div className="max-w-7xl w-full mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <span className="inline-flex items-center justify-center rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 text-white w-12 h-12 shadow-lg">
                <Clock size={24} />
              </span>
              <div>
                <h1 className="text-3xl font-bold tracking-tight">Horaires de service</h1>
                <p className={`text-sm ${subtleText}`}>
                  Définissez précisément les sessions d’ouverture de votre établissement.
                </p>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => loadServiceHours(true)}
              disabled={refreshing}
              className={`${ghostButtonClasses} inline-flex items-center gap-2 ${refreshing ? "opacity-60" : ""}`}
            >
              <RefreshCw size={18} className={refreshing ? "animate-spin" : ""} />
              Actualiser
            </button>
            <button
              onClick={() => setShowModal(true)}
              className={`${primaryButtonClasses} inline-flex items-center gap-2`}
            >
              <Plus size={18} />
              Ajouter un horaire
            </button>
          </div>
        </div>

        {/* Informations sur les sessions */}
        <div className={`${cardClasses} rounded-3xl p-6`}>
          <div className="flex items-start gap-4">
            <div className="rounded-2xl bg-gradient-to-br from-sky-500 to-indigo-500 text-white p-3 shadow-lg">
              <AlertCircle size={22} />
            </div>
            <div className={`text-sm ${subtleText} space-y-3`}>
              <div>
                <p className="font-semibold text-base text-current mb-1">Concept de sessions</p>
                <p>
                  Une session correspond à une période de service continue. Elle peut s’étendre sur le lendemain,
                  mais ne doit pas se chevaucher avec une autre session pour le même jour.
                </p>
              </div>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <li className={`${isDarkMode ? "bg-white/5" : "bg-slate-50"} rounded-2xl px-3 py-2 text-xs`}>
                  • Session = plage d’ouverture complète
                </li>
                <li className={`${isDarkMode ? "bg-white/5" : "bg-slate-50"} rounded-2xl px-3 py-2 text-xs`}>
                  • Possible de terminer le lendemain
                </li>
                <li className={`${isDarkMode ? "bg-white/5" : "bg-slate-50"} rounded-2xl px-3 py-2 text-xs`}>
                  • Identifiée par sa date & heure de début
                </li>
                <li className={`${isDarkMode ? "bg-white/5" : "bg-slate-50"} rounded-2xl px-3 py-2 text-xs`}>
                  • Pas de chevauchement autorisé
                </li>
              </ul>
              <div className={`${isDarkMode ? "bg-white/5" : "bg-slate-50"} rounded-2xl px-4 py-3`}>
                <p className="font-medium text-sm mb-1">Exemple</p>
                <p className="text-xs">
                  Vendredi 18h00 → Samedi 02h00 = “Session du vendredi 18h00”.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Planning hebdomadaire */}
        <div className={`${cardClasses} rounded-3xl`}>
          {refreshing && (
            <div className={`p-4 ${isDarkMode ? "bg-white/5" : "bg-slate-50"} rounded-t-3xl`}>
              <div className="flex items-center justify-center gap-2 text-sm text-amber-500">
                <RefreshCw size={16} className="animate-spin" />
                <span>Mise à jour en cours...</span>
              </div>
            </div>
          )}
          
          <div className="p-6">
            <h2 className="text-xl font-semibold mb-6 flex items-center gap-2">
              <Calendar size={24} className="text-emerald-400" />
              Planning hebdomadaire
            </h2>
            
            <div className="relative flex flex-col gap-4 md:grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-7 md:gap-4">
              <span className={`pointer-events-none absolute left-6 top-4 bottom-4 w-[2px] rounded-full md:hidden ${timelineLineClass}`} aria-hidden />
              {daysOfWeek.map((day) => {
                const dayHours = groupedHours[day.value] || [];
                const DayIcon = day.icon;
                
                return (
                  <div
                    key={day.value}
                    className={`relative md:static ${isDarkMode ? "bg-white/5 border-white/10" : "bg-white border-slate-200"} border rounded-3xl p-5 flex flex-col gap-4 pl-12 md:pl-5 shadow-md/40 hover:shadow-amber-500/10 transition`}
                  >
                    <span className={`md:hidden pointer-events-none absolute left-4 top-7 w-4 h-4 rounded-full border-2 ${timelineDotBorder} bg-amber-400 shadow-lg`} aria-hidden />
                    <div className="flex flex-col gap-2 min-w-0">
                      <div className="flex items-center gap-2">
                        <div className="p-2 rounded-2xl bg-amber-400/15 text-amber-400">
                          <DayIcon size={16} />
                        </div>
                        <h3 className="font-semibold text-base">{day.name}</h3>
                      </div>
                      <span
                        className={`inline-flex items-center px-3 py-1 text-[11px] font-semibold rounded-full whitespace-nowrap ${
                          dayHours.length
                            ? "bg-emerald-500/10 text-emerald-500"
                            : "bg-slate-200 text-slate-600 dark:bg-white/10 dark:text-slate-200"
                        }`}
                      >
                        {dayHours.length ? `${dayHours.length} session${dayHours.length > 1 ? "s" : ""}` : "Aucune session"}
                      </span>
                    </div>
                    
                    <div className="space-y-3">
                      {dayHours.length === 0 ? (
                        <div className="text-center py-6 flex flex-col items-center gap-2">
                          <Moon size={26} className={subtleText} />
                          <p className={`text-xs ${subtleText}`}>Aucune session planifiée</p>
                        </div>
                      ) : (
                        dayHours
                          .sort((a, b) => a.start_time.localeCompare(b.start_time))
                          .map((hour) => (
                            <div 
                              key={hour.id}
                              className={`p-3 rounded-2xl border transition ${
                                hour.is_active
                                  ? isDarkMode
                                    ? "border-emerald-400/40 bg-emerald-500/10"
                                    : "border-emerald-200 bg-emerald-50"
                                  : isDarkMode
                                    ? "border-white/10 bg-white/5 opacity-70"
                                    : "border-slate-200 bg-slate-50 opacity-70"
                              }`}
                            >
                              <div className="flex items-center justify-between mb-1">
                                <span className={`text-xs font-mono ${subtleText}`}>
                                  {formatTimeRange(hour.start_time, hour.end_time)}
                                </span>
                                <div className="flex items-center space-x-1">
                                  <button
                                    onClick={() => handleToggleStatus(hour.id)}
                                    className="p-1 rounded transition text-emerald-400 hover:text-emerald-300"
                                    title={hour.is_active ? 'Désactiver' : 'Activer'}
                                  >
                                    {hour.is_active ? <Eye size={12} /> : <EyeOff size={12} />}
                                  </button>
                                  <button
                                    onClick={() => handleEdit(hour)}
                                    className="p-1 text-sky-400 hover:text-sky-300 transition"
                                    title="Modifier"
                                  >
                                    <Edit size={12} />
                                  </button>
                                  <button
                                    onClick={() => handleDelete(hour.id)}
                                    className="p-1 text-rose-400 hover:text-rose-300 transition"
                                    title="Supprimer"
                                  >
                                    <Trash2 size={12} />
                                  </button>
                                </div>
                              </div>
                              {hour.service_name && (
                                <p className={`text-xs ${subtleText} truncate`}>
                                  {hour.service_name}
                                </p>
                              )}
                              {isTimeCrossDay(hour.start_time, hour.end_time) && (
                                <div className="mt-1">
                                  <span className="text-xs inline-flex px-2 py-1 rounded-full bg-orange-500/10 text-orange-500">
                                    Multi-jour
                                  </span>
                                </div>
                              )}
                            </div>
                          ))
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Modal d'ajout/modification */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 px-4">
          <div className={`${cardClasses} rounded-3xl p-6 w-full max-w-md`}>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Clock size={24} className="text-amber-400" />
                {editingHour ? 'Modifier l\'horaire' : 'Ajouter un horaire'}
              </h2>
              <button
                onClick={handleCloseModal}
                className={`${subtleText} hover:text-amber-400 transition`}
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className={`block text-sm font-semibold mb-2 ${subtleText}`}>
                  Jour de la semaine
                </label>
                <select
                  value={formData.day_of_week}
                  onChange={(e) => setFormData({ ...formData, day_of_week: parseInt(e.target.value) })}
                  className={`w-full px-3 py-2 rounded-xl ${inputClasses}`}
                  required
                >
                  {daysOfWeek.map((day) => (
                    <option key={day.value} value={day.value}>
                      {day.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-semibold mb-2 ${subtleText}`}>
                    Heure de début
                  </label>
                  <select
                    value={formData.start_time}
                    onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                    className={`w-full px-3 py-2 rounded-xl font-mono ${inputClasses}`}
                    required
                  >
                    {commonHours.map((hour) => (
                      <option key={hour} value={hour}>
                        {hour}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={`block text-sm font-semibold mb-2 ${subtleText}`}>
                    Heure de fin
                  </label>
                  <select
                    value={formData.end_time}
                    onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                    className={`w-full px-3 py-2 rounded-xl font-mono ${inputClasses}`}
                    required
                  >
                    {commonHours.map((hour) => (
                      <option key={hour} value={hour}>
                        {hour}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Indication si débordement sur le lendemain */}
              {isTimeCrossDay(formData.start_time, formData.end_time) && (
                <div className="p-3 bg-orange-500/10 border border-orange-400/30 rounded-2xl">
                  <div className="flex items-center gap-2 text-orange-300">
                    <AlertCircle size={16} />
                    <span className="text-sm font-medium">Session multi-jour détectée</span>
                  </div>
                  <p className="text-xs text-orange-200 mt-1">
                    Cette session commence le {getDayName(formData.day_of_week)} à {formData.start_time} 
                    et se termine le lendemain à {formData.end_time}.
                  </p>
                </div>
              )}

              <div>
                <label className={`block text-sm font-semibold mb-2 ${subtleText}`}>
                  Nom du service (optionnel)
                </label>
                <input
                  type="text"
                  value={formData.service_name}
                  onChange={(e) => setFormData({ ...formData, service_name: e.target.value })}
                  className={`w-full px-3 py-2 rounded-xl ${inputClasses}`}
                  placeholder="Ex: Service du déjeuner, Service du soir..."
                />
              </div>

              <div className="flex items-center gap-2 text-sm font-medium">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="rounded accent-amber-500"
                />
                <label htmlFor="is_active" className={subtleText}>
                  Horaire actif
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className={`${ghostButtonClasses} px-4`}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className={`${primaryButtonClasses} inline-flex items-center gap-2`}
                >
                  <Check size={16} />
                  {editingHour ? 'Modifier' : 'Ajouter'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceHoursManagement; 
